import { useQuery } from "@tanstack/react-query";
import { PageLoader, useMinLoading } from "@/components/Preloader";
import api from "@/lib/api";
import { Article } from "@/lib/types";
import { useAuth } from "@/context/AuthContext";
// CategoryRail "Jump back in" strip superseded by JumpBackInSection (locked Smart Resume rebuild)
import { JumpBackInSection } from "@/components/home/jumpbackin/JumpBackInSection";
import { SponsorBanner } from "@/components/home/SponsorBanner";
import { LeadCarousel } from "@/components/home/LeadCarousel";
import { LayerIndexCard } from "@/components/home/LayerIndexCard";
import { MarketSnapshot } from "@/components/home/MarketSnapshot";
import { Newsstream } from "@/components/home/Newsstream";
import { IntelligenceRow } from "@/components/home/IntelligenceRow";
import { HOME_CONTAINER } from "@/components/home/shared";
import { TrendingSectorRail } from "@/components/home/TrendingSectorRail";
import { OriginalsSection } from "@/components/home/OriginalsSection";
import { AuthorsRail } from "@/components/home/AuthorsRail";
// MarketIntelligenceSection (components/home/MarketIntelligenceSection) superseded by the approved
// V2 MarketIntelligenceField (locked 3D terrain, Figma 230:204) — file kept for rollback
import { MarketIntelligenceField } from "@/components/home2/MarketIntelligenceField";
// SectorExplorer (components/home/SectorExplorer) superseded by SignalPortals — file kept for rollback
import { SignalPortals } from "@/components/home2/SignalPortals";
import { RegulationIntelligenceSection } from "@/components/home/RegulationIntelligenceSection";
import { ResearchSection } from "@/components/home/ResearchSection";
import { PremiumSection } from "@/components/home/premium/PremiumSection";
// MoreStories (components/home/MoreStories) superseded by the locked More Stories rebuild — file kept for rollback
import { MoreStoriesSection } from "@/components/home/morestories/MoreStoriesSection";
import { MostApplaudedRail } from "@/components/home/MostApplaudedRail";
import { Reveal } from "@/components/home/shared";

export default function Home() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["homepage"],
    queryFn: async () => (await api.get("/homepage")).data,
    staleTime: 60 * 1000,
  });

  const showLoader = useMinLoading(isLoading);
  if (showLoader) {
    return <PageLoader />;
  }

  const hero: Article | null = data?.hero;
  const slides: Article[] = data?.breaking_carousel?.length
    ? data.breaking_carousel
    : hero ? [hero] : [];
  const latest: Article[] = data?.latest || [];
  const moreStories = latest.slice(8, 16);
  const moreExclude: string[] = [
    ...slides.map((s: Article) => s.slug),
    ...((data?.originals || []) as Article[]).map((a) => a.slug),
    ...((data?.premium_dives || []) as Article[]).map((a) => a.slug),
  ];
  const seenJump = new Set<string>();
  const jumpFallback: Article[] = [
    ...latest.slice(16),
    ...((data?.originals || []) as Article[]),
    ...((data?.premium_dives || []) as Article[]),
  ].filter((a) => (seenJump.has(a.slug) ? false : (seenJump.add(a.slug), true)));

  return (
    <div data-testid="homepage">
      <div className={`${HOME_CONTAINER} py-5 sm:py-6`}>
        <SponsorBanner />

        {slides.length ? (
          <section data-testid="front-layer"
            className="grid grid-cols-1 lg:grid-cols-[minmax(0,52fr)_minmax(0,20fr)_minmax(0,28fr)] gap-4 fade-up">
            <LeadCarousel slides={slides} />
            <LayerIndexCard />
            <div className="lg:row-span-2 flex flex-col gap-4 min-w-0">
              <MarketSnapshot />
              <Newsstream />
            </div>
            <div className="lg:col-span-2 min-w-0">
              <IntelligenceRow regs={data?.regulation_watch || []} brief={data?.daily_brief || null}
                premiumFeature={data?.premium_feature || null} />
            </div>
          </section>
        ) : (
          <>
            <div className="text-center py-24 border border-dashed border-white/10 rounded-xl">
              <p className="text-gray-400">Fresh stories are on the way — check back in a minute.</p>
            </div>
            <div className="mt-4">
              <IntelligenceRow regs={data?.regulation_watch || []} brief={data?.daily_brief || null}
                premiumFeature={data?.premium_feature || null} />
            </div>
          </>
        )}

        <TrendingSectorRail />

        <Reveal>
          <SignalPortals />
        </Reveal>

        <Reveal>
          <OriginalsSection originals={data?.originals || []} />
        </Reveal>

        <Reveal>
          <AuthorsRail />
        </Reveal>

        <Reveal>
          <MostApplaudedRail />
        </Reveal>

        <Reveal>
          <MarketIntelligenceField />
        </Reveal>

        <Reveal>
          <RegulationIntelligenceSection />
        </Reveal>

        <Reveal>
          <ResearchSection research={data?.research} />
        </Reveal>

        <Reveal>
          <PremiumSection dives={data?.premium_dives || []} />
        </Reveal>

        <Reveal>
          <MoreStoriesSection articles={moreStories} excludeSlugs={moreExclude} />
        </Reveal>

        <Reveal>
          <JumpBackInSection fallback={jumpFallback} />
        </Reveal>
      </div>
    </div>
  );
}

